import { observer } from 'mobx-react';
import React from 'react';
import DeliveryRequestInfoModalTopBar from './components/DeliveryRequestInfoModalTopBar';
import { modalStore } from 'src/mobx/modalStore';
import { formatPhone, truncateLongString } from 'src/utils/formatter';
import { formatDate, getTranslate } from 'src/utils/helpers';
import ButtonSwitcher, { IButtonSwitcherOption } from 'src/view/components/Controls/ButtonSwitcher/ButtonSwitcher';
import CopyButton from 'src/view/components/Controls/CopyButton/CopyButton';
import CustomModalComponent from 'src/view/components/Controls/Modal/Modal';
import ModalBackground from 'src/view/components/Controls/Modal/ModalBackground/ModalBackground';
import ModalHeader from 'src/view/components/Controls/Modal/ModalHeader/ModalHeader';
import TimeLine from 'src/view/components/Controls/TimeLine/TimeLine';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import { DELIVERY_REQUEST_STATUS } from 'src/view/screens/DeliveryManagementScreen/screens/DeliveryTrackingScreen/constants/deliveryTracking';
import {
  getDriverInfo,
  getStatusColor,
  isNotFullTimeLine,
} from 'src/view/screens/DeliveryManagementScreen/screens/DeliveryTrackingScreen/helpers/deliveryTracking';
import { deliveryTrackingStore } from 'src/view/screens/DeliveryManagementScreen/screens/DeliveryTrackingScreen/store/deliveryTrackingStore';
import { TTakerGoDetails } from 'src/view/screens/DeliveryManagementScreen/screens/DeliveryTrackingScreen/types/deliveryTracking';
import Label from '~/view/components/Controls/Label';
import './style.scss';

type TTimeLine = 'takergo_timeline' | 'takergo_request_timeline';

interface IState {
  timeLine: TTimeLine;
}

@observer
export default class DeliveryRequestInfoModal extends React.Component<{}, IState> {
  timeLineOptions: IButtonSwitcherOption[] = [
    { label: 'Full timeline', key: 'takergo_timeline' },
    { label: 'Request timeline', key: 'takergo_request_timeline' },
  ];

  state: IState = {
    timeLine: 'takergo_timeline',
  };

  componentDidMount = async () => {
    const deliveryRequest = modalStore.deliveryRequestInfoModal?.deliveryRequest;
    if (deliveryRequest) await deliveryTrackingStore.fetchRequestDetails(deliveryRequest.id);
  };

  renderTimeLineLeading = (event: TTakerGoDetails) => {
    return (
      <div className="d-flex flex-row align-items-center h-min-content">
        {/* need data from back */}
        <div className="text-size-16 family-medium mr-3 color-gray2">{1}</div>
        <div className="d-flex flex-column">
          <div className="text-size-14 family-semibold">{event.dsp_name}</div>
          <div className="text-size-12 family-medium color-mainblue">{truncateLongString(event.dsp_order_id)}</div>
        </div>
      </div>
    );
  };

  renderTimeLineTrailing = (event: TTakerGoDetails) => {
    return (
      <>
        <div className="d-flex flex-row justify-content-between align-items-center">
          <Label text={event.dsp_status} />
          <p className="family-regular text-size-14 px-3">{formatDate(event.created_at, {})}</p>
        </div>
        {event.taker_go_status === DELIVERY_REQUEST_STATUS.ASSIGNED ? (
          <div className="d-flex delivery-request-info-modal__timeline-driver-info">
            <p className="family-bold letter-spacing-12 text-uppercase text-size-10 color-gray2">{getDriverInfo(event)}</p>
          </div>
        ) : null}
      </>
    );
  };

  render() {
    const deliveryRequest = modalStore.deliveryRequestInfoModal?.deliveryRequest;
    if (!deliveryRequest) return;

    return (
      <CustomModalComponent isOpen onRequestClose={modalStore.closeDeliveryRequestInfoModal} withCloseIcon modalMaxWidth={950}>
        <ModalBackground>
          <ModalHeader title={getTranslate(deliveryRequest.dsp_name)} wrapperClassName="align-items-center" titleClassName="text-size-24">
            <div className="family-medium text-size-24 ml-2 color-gray2">{truncateLongString(deliveryRequest.dsp_order_id)}</div>
            <CopyButton data={deliveryRequest.dsp_order_id} tooltipMessage="copied_to_clickboard" tooltipProps={{ gapTop: -5 }} />
            <Label text={deliveryRequest.dsp_status} color={getStatusColor(deliveryRequest.taker_go_status)} className="ml-4" />
          </ModalHeader>
          <div className="d-flex flex-column flex-1 px-8 py-6">
            <DeliveryRequestInfoModalTopBar deliveryRequest={deliveryRequest} />
            <ButtonSwitcher
              className="delivery-request-info-modal__switcher"
              buttonClassName="delivery-request-info-modal__switcher-btn"
              onClick={(option) => this.setState({ timeLine: option.key as TTimeLine })}
              options={this.timeLineOptions}
              selected={this.state.timeLine}
            />
            <div className="d-flex flex-row flex-1 mt-4">
              <ScrollableBlock className="h-auto mr-7 w-70" showScrollbar scrollbarSize="small">
                <TimeLine<TTakerGoDetails>
                  data={deliveryTrackingStore.selected.details[this.state.timeLine]}
                  leading={this.renderTimeLineLeading}
                  trailing={this.renderTimeLineTrailing}
                  loading={deliveryTrackingStore.selected.loading}
                  notFull={isNotFullTimeLine(deliveryTrackingStore.selected.request?.taker_go_status)}
                />
              </ScrollableBlock>
              <ScrollableBlock showScrollbar scrollbarSize="small" className="h-auto w-40">
                <div className="flex-1 mb-5">
                  <h3 className="family-regular text-size-18 letter-spacing-12 text-uppercase mb-4">From</h3>
                  <p className="family-bold text-size-16 text-break mb-2">{getTranslate(deliveryRequest.branch.name)}</p>
                  <p className="family-regular text-size-14">{formatPhone(deliveryRequest.branch.mobile)}</p>
                  <p className="family-regular text-size-14">{deliveryRequest.branch.address}</p>
                </div>
                <div className="d-flex flex-column mb-5">
                  <h3 className="family-regular text-size-18 letter-spacing-12 text-uppercase mb-4">To</h3>
                  <p className="family-bold text-size-16 mb-2">{deliveryRequest.customer.first_name}</p>
                  <p className="family-regular text-size-14">{formatPhone(deliveryRequest.customer.mobile)}</p>
                  <p className="family-regular text-size-14">{deliveryRequest.customer.address}</p>
                </div>
                {deliveryRequest.driver_found_at ? (
                  <div className="d-flex flex-column mb-5">
                    <h3 className="family-regular text-size-18 letter-spacing-12 text-uppercase mb-4">Driver</h3>
                    <p className="family-bold text-size-16 mb-2">{deliveryRequest.driver_name}</p>
                    <p className="family-regular text-size-14">{formatPhone(deliveryRequest.driver_mobile)}</p>
                  </div>
                ) : null}
              </ScrollableBlock>
            </div>
          </div>
        </ModalBackground>
      </CustomModalComponent>
    );
  }
}
